import styled, { css, keyframes } from 'styled-components'
import { PasswordToggle } from 'Components/IconInput'

/**
 * Shared layout for the Urban Sports Club voucher screens.
 *
 * Deliberately plain and light: the flow is white-label by default (see UscHeader),
 * so the page carries BodyFast's own typography and neutral surfaces rather than
 * partner styling. USC brand colours are never applied to our page — the Brand
 * Guide forbids using their palette outside their own logotype rules.
 */

const PAGE_GUTTER_PX = 20

export const USC_TOTAL_STEPS = 3

const FOCUS_RING_COLOR = 'rgba(16, 178, 148, 0.18)'

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
`

const dockIn = keyframes`
  from { transform: translateY(100%); }
  to { transform: none; }
`

const growIn = keyframes`
  from { width: var(--usc-progress-from); }
  to { width: var(--usc-progress); }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
`

/** Every animation here is decorative, so it stays behind the OS motion switch. */
const motion = (animation: ReturnType<typeof keyframes>, duration: string) => css`
  @media (prefers-reduced-motion: no-preference) {
    animation: ${animation} ${duration} ease-out both;
  }
`

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 0 ${PAGE_GUTTER_PX}px;
  background-color: ${({ theme }) => theme.colors.basicWhite};
`

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  max-width: 420px;
  ${motion(fadeUp, '0.45s')}

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: 0 1 auto;
    margin: ${({ theme }) => theme.spacing.small} 0 ${({ theme }) => theme.spacing.xLarge};
    padding: ${({ theme }) => theme.spacing.xLarge};
    border: 1px solid ${({ theme }) => theme.colors.grey100};
    border-radius: 20px;
    box-shadow: 0 12px 32px rgba(3, 48, 44, 0.07);
  }
`

export const StepLabel = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const Progress = styled.div`
  height: 4px;
  width: 100%;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.colors.grey100};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.mediumLarge};
`

/**
 * Takes the step it belongs to rather than a width, so each screen picks the bar
 * up where the previous one left it instead of re-filling from zero.
 */
export const ProgressFill = styled.div<{ step: number }>`
  --usc-progress: ${({ step }) => (step / USC_TOTAL_STEPS) * 100}%;
  --usc-progress-from: ${({ step }) => ((step - 1) / USC_TOTAL_STEPS) * 100}%;
  height: 100%;
  width: var(--usc-progress);
  border-radius: inherit;
  background-color: ${({ theme }) => theme.colors.primary};
  ${motion(growIn, '0.7s')}
`

export const Title = styled.h1`
  color: ${({ theme }) => theme.colors.green950};
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: 600;
  line-height: 130%;
  letter-spacing: -0.01em;
  margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
`

export const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  line-height: 160%;
  margin: 0 0 ${({ theme }) => theme.spacing.mediumLarge} 0;
`

/**
 * The same-email notice (§2.4). Prominent on purpose: we cannot verify the address
 * against USC, and matching emails are what lets USC attribute the redemption.
 */
export const Notice = styled.p`
  background-color: ${({ theme }) => theme.colors.green50};
  border: 1px solid ${({ theme }) => theme.colors.green200};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 0;
  color: ${({ theme }) => theme.colors.green900};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 600;
  line-height: 160%;
  margin: 0;
  padding: ${({ theme }) => theme.spacing.mediumSmall} ${({ theme }) => theme.spacing.medium};
`

export const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.red800};
  font-size: ${({ theme }) => theme.fontSizes.small};
  line-height: 150%;
  margin-top: ${({ theme }) => theme.spacing.small};
  ${motion(fadeIn, '0.2s')}
`

export const Footnote = styled.p`
  color: ${({ theme }) => theme.colors.lightText};
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  text-align: center;
  margin: ${({ theme }) => theme.spacing.small} 0 0 0;
`

/**
 * Terms and Privacy Policy consent line. Sits on the code screen: an account is
 * created for the member as part of the redemption, so the links have to be there
 * before the flow starts, same as on the Wellhub entry screen.
 */
export const Disclaimer = styled.p`
  color: ${({ theme }) => theme.colors.lightText};
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  line-height: 150%;
  text-align: center;
  margin: ${({ theme }) => theme.spacing.small} 0 0 0;
`

export const LegalLink = styled.a`
  color: ${({ theme }) => theme.colors.green950};
  text-decoration: underline;
`

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  /* Grows so the action bar's auto top margin reaches the bottom edge. */
  flex: 1;
  gap: ${({ theme }) => theme.spacing.mediumSmall};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: 0 1 auto;
  }
`

export const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  /* The field's visible border lives on IconInput's wrapper element, so the focus
     ring and the comfortable tap height are applied from here rather than by
     restyling the shared input for every other flow that uses it. */
  > div {
    min-height: 52px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  > div:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${FOCUS_RING_COLOR};
  }

  /* The show/hide toggle and the valid-input check sit side by side in the password
     field and read as one control at the shared spacing. */
  ${PasswordToggle} {
    margin-right: ${({ theme }) => theme.spacing.mediumSmall};
  }
`

export const ActionBar = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  bottom: 0;
  margin: auto -${PAGE_GUTTER_PX}px 0;
  padding: ${({ theme }) => theme.spacing.mediumSmall} ${PAGE_GUTTER_PX}px;
  padding-bottom: calc(${({ theme }) => theme.spacing.mediumSmall} + env(safe-area-inset-bottom));
  background-color: ${({ theme }) => theme.colors.basicWhite};
  border-top: 1px solid ${({ theme }) => theme.colors.grey100};
  ${motion(dockIn, '0.35s')}

  button {
    width: 100%;
    max-width: none;
    min-height: 56px;
    margin: 0;
    transition: transform 0.12s ease, opacity 0.2s ease;
  }

  button:active:not(:disabled) {
    transform: scale(0.985);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: static;
    margin: ${({ theme }) => theme.spacing.small} 0 0;
    padding: 0;
    border-top: none;
    animation: none;
  }
`
